import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  EventEmitter,
  inject,
  Input,
  Output,
  signal,
  ViewChild,
} from '@angular/core';
import {
  AbstractControl,
  FormControl,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { GalaxyFilterInterface } from '@vendasta/galaxy/filter/chips';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { GalaxyPopoverModule, PopoverComponent, PopoverPositions } from '@vendasta/galaxy/popover';
import { GalaxyEmptyStateModule } from '@vendasta/galaxy/empty-state';
import { PresetFilter, PresetFilters, SavedFilter, SavedFilters } from './interfaces';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { GalaxyButtonGroupModule } from '@vendasta/galaxy/button-group';
import { OpenConfirmationModalService } from '@vendasta/galaxy/confirmation-modal';
import { firstValueFrom } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';
import { BREAKPOINT_WIDTH } from '../../constants';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';

type Mode = 'list' | 'create';

@Component({
  selector: 'crm-filter-view',
  templateUrl: './filter-view.component.html',
  styleUrls: ['./filter-view.component.scss'],
  imports: [
    CommonModule,
    GalaxyButtonGroupModule,
    GalaxyFormFieldModule,
    GalaxyPopoverModule,
    GalaxyEmptyStateModule,
    MatButtonModule,
    MatIconModule,
    MatMenuModule,
    ReactiveFormsModule,
    TranslateModule,
    MatSlideToggleModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FilterViewComponent {
  private _filters = signal<GalaxyFilterInterface[]>([]);
  @Input({ required: true })
  set filters(value: GalaxyFilterInterface[]) {
    const currentFilters = this._filters();
    this._filters.set(value);
    if (!this.filterAreEqual(currentFilters, value)) {
      this.selectedViewFilter.set(undefined);
    }
    for (const [key, entry] of this.toggleMap()) {
      if (entry) {
        this.selectedToggleFilter.set(key);
        break;
      }
    }
  }
  @Output() filtersChange = new EventEmitter<GalaxyFilterInterface[]>();

  private _keys = signal<string[]>([]);
  @Input({ required: true })
  set keys(value: string[]) {
    this._keys.set(value);
  }

  private _presetFilters = signal<PresetFilters>([]);
  @Input() set presetFilters(value: PresetFilters) {
    const filters = value?.map((f) => ({ ...f, preset: true }) as PresetFilter);
    this._presetFilters.set(filters ?? []);
  }
  viewFilters = computed(() => {
    return this._presetFilters().filter((p) => !p.toggle);
  });
  toggleFilters = computed(() => {
    return this._presetFilters().filter((p) => !!p.toggle);
  });
  toggleMap = computed(() => {
    const newMap = new Map<string, boolean>();
    for (const f of this.toggleFilters()) {
      const matchFilters = f.filters.every(
        (item) =>
          this._filters().findIndex((f2) => {
            return this.filterAreEqual([item], [f2]);
          }) !== -1,
      );
      newMap.set(f.name, matchFilters);
    }
    return newMap;
  });

  @ViewChild('viewsMenu') viewsMenu?: PopoverComponent;

  private readonly snackbarService = inject(SnackbarService);
  private readonly confirmationModalService = inject(OpenConfirmationModalService);
  private readonly translateService = inject(TranslateService);

  private readonly i18n = {
    views: toSignal<string>(this.translateService.stream('VIEWS.VIEWS')),
  };

  protected mode: Mode = 'list';
  protected readonly savedFilters = signal<SavedFilters>([]);
  protected readonly allFilters = computed(() => [
    ...this.sortFilters(this.savedFilters()),
    ...this.sortFilters(this.viewFilters()),
  ]);
  protected readonly hasFilters = computed(() => Object.keys(this.viewFilters()).length > 0);
  protected readonly newViewNameCtrl = new FormControl<string>('', {
    validators: [Validators.required, this.nameExistsValidator(), this.nameLengthValidator()],
  });
  protected readonly mobileWidth = BREAKPOINT_WIDTH.MOBILE;
  protected readonly popoverPosition = [
    PopoverPositions['BottomRight'],
    PopoverPositions['BottomLeft'],
    PopoverPositions['Right'],
    PopoverPositions['Left'],
  ];
  private readonly selectedToggleFilter = signal<string | undefined>(undefined);
  protected readonly selectedViewFilter = signal<string | undefined>(undefined);
  protected readonly selectedViewFilterName = computed(() => this.selectedViewFilter() ?? this.i18n.views());
  protected readonly selectedToggleFilterValues = computed(() => {
    const toggleFilters = this.toggleFilters() || [];
    return toggleFilters.find((f) => f.name === this.selectedToggleFilter())?.filters ?? [];
  });
  protected readonly filtersExceptToggled = computed(() => {
    const filters = [...this._filters()];
    return filters.filter((f) => {
      return !this.selectedToggleFilterValues().find((f2) => {
        return f2.filterId === f.filterId;
      });
    });
  });

  constructor() {
    effect(() => {
      this.loadSavedFilters(this._keys());
    });
  }

  protected handleCancelClick(): void {
    this.mode = 'list';
  }

  protected handleCreateViewClick(): void {
    if (this._filters().length > 0) {
      this.newViewNameCtrl.setValue('');
      this.newViewNameCtrl.markAsUntouched();
      this.mode = 'create';
    } else {
      this.snackbarService.openErrorSnack('VIEWS.ERRORS.NO_FILTERS');
    }
  }

  protected async handleDeleteFilterClick(filter: SavedFilter): Promise<void> {
    const confirm = await firstValueFrom(
      this.confirmationModalService.openModal({
        title: 'VIEWS.DELETE_CONFIRMATION.TITLE',
        cancelOnEscapeKeyOrBackgroundClick: true,
        message: this.translateService.instant('VIEWS.DELETE_CONFIRMATION.MESSAGE', { viewName: filter.name }),
        type: 'warn',
        confirmButtonText: 'ACTIONS.DELETE',
      }),
    );
    if (confirm) {
      if (this.selectedViewFilter() === filter.name) {
        this.selectedViewFilter.set(undefined);
      }
      const savedFilters = this.savedFilters().filter((f) => f.name !== filter.name);
      this.savedFilters.set(savedFilters);
      localStorage.setItem(this._keys()[0], JSON.stringify(savedFilters));
    }
  }

  protected handleSaveClick(): void {
    this.newViewNameCtrl.markAsTouched();
    if (this.newViewNameCtrl.valid) {
      const name = this.newViewNameCtrl.value!;
      const savedFilter: SavedFilter = { name, filters: this._filters() };
      if (this.selectedToggleFilter()) {
        savedFilter.filters = this.filtersExceptToggled();
      }
      const savedFilters = this.sortFilters(this.savedFilters().concat(savedFilter));
      if (this._keys()[0]) {
        localStorage.setItem(this._keys()[0], JSON.stringify(savedFilters));
      }
      this.savedFilters.set(savedFilters);
      this.selectedViewFilter.set(name);
      this.viewsMenu?.close();
    }
  }

  protected toggleFilter(filter: PresetFilter, checked: boolean): void {
    const otherFilters = this.filtersExceptToggled();
    if (checked) {
      this.selectedToggleFilter.set(filter.name);
      this._filters.set([...filter.filters, ...otherFilters]);
      this.filtersChange.emit([...filter.filters, ...otherFilters]);
    } else {
      this._filters.set(otherFilters);
      this.filtersChange.emit(otherFilters);
      this.selectedToggleFilter.set(undefined);
    }
  }

  protected handleSelectFilterClick(filter: PresetFilter | SavedFilter): void {
    this.selectedViewFilter.set(filter.name);
    const toggleFilters = this.selectedToggleFilterValues();
    this._filters.set([...filter.filters, ...toggleFilters]);
    this.filtersChange.emit([...filter.filters, ...toggleFilters]);
    this.viewsMenu?.close();
  }

  protected handleViewsClick(): void {
    this.viewsMenu?.open();
    this.mode = 'list';
  }

  private filterAreEqual(a: GalaxyFilterInterface[], b: GalaxyFilterInterface[]): boolean {
    // remove preset filters of the comparison
    const filterOutPresetsFn = (f: GalaxyFilterInterface): boolean => !f.definition?.preset;
    // filter out irrelevant attributes
    const mapRelevantFieldsFn = (f: GalaxyFilterInterface) => ({
      fieldId: f.fieldId,
      operator: f.operator,
      values: f.values,
    });

    let filterA = a.filter(filterOutPresetsFn).map(mapRelevantFieldsFn);
    let filterB = b.filter(filterOutPresetsFn).map(mapRelevantFieldsFn);
    // sort by fieldId
    filterA = filterA.sort((x, y) => x.fieldId.localeCompare(y.fieldId));
    filterB = filterB.sort((x, y) => x.fieldId.localeCompare(y.fieldId));
    // compare
    return JSON.stringify(filterA) === JSON.stringify(filterB);
  }

  private loadSavedFilters(keys: string[]): void {
    for (const key of keys) {
      const savedFilters = key ? localStorage.getItem(key) : undefined;
      if (savedFilters) {
        try {
          const filters = JSON.parse(savedFilters);
          this.savedFilters.set(this.sortFilters(filters));
          break;
        } catch {
          this.savedFilters.set([]);
          localStorage.removeItem(key);
        }
      }
    }
  }

  private nameExistsValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value as string;
      const exists = Boolean(this.allFilters().find((f) => f.name === value));
      return exists ? { presetName: true } : null;
    };
  }

  private nameLengthValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value as string;
      if (value.length > 36) {
        return { nameLength: true };
      }
      return null;
    };
  }

  private sortFilters(filters: SavedFilters): SavedFilters {
    return filters.sort((a, b) => a.name.toLocaleLowerCase().localeCompare(b.name.toLocaleLowerCase()));
  }
}
